import React from 'react';
import { Box, Grid, Typography, Paper, Button, useTheme, alpha } from '@mui/material';
import SectionContainer from './SectionContainer';
import AnimatedElement from './AnimatedElement';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TelegramIcon from '@mui/icons-material/Telegram';
import DevicesIcon from '@mui/icons-material/Devices';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AnalyticsIcon from '@mui/icons-material/Analytics';

const FeatureItem = ({ icon, title, description, delay = 0 }) => {
  const theme = useTheme();
  
  return (
    <AnimatedElement animation="fadeInLeft" delay={delay}>
      <Box sx={{ display: 'flex', mb: 4, gap: 3 }}>
        <Box 
          sx={{ 
            color: theme.palette.secondary.main,
            display: 'flex',
            alignItems: 'flex-start',
            pt: 0.5
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>
    </AnimatedElement>
  );
};

const SolutionSection = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <TelegramIcon sx={{ fontSize: 32 }} />,
      title: 'Вебинары прямо в Telegram',
      description: 'Зрителям не нужно покидать привычную среду и переходить в браузер. Вебинар открывается прямо в интерфейсе мессенджера.',
      delay: 0.2,
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 32 }} />,
      title: 'Верифицированные контакты',
      description: 'Автоматическое получение реальных контактов участников из их Telegram-аккаунтов для дальнейшего общения.',
      delay: 0.4,
    },
    {
      icon: <DevicesIcon sx={{ fontSize: 32 }} />,
      title: 'Удобство для организаторов',
      description: 'Создание и настройка вебинара, управление слайдами и кнопками призыва к действию через интуитивно понятный интерфейс.',
      delay: 0.6,
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      title: 'Аналитика присутствия',
      description: 'Подробная статистика по участникам вебинара, их активности и взаимодействию с призывами к действию.',
      delay: 0.8,
    },
  ];
  
  return (
    <SectionContainer
      id="solution"
      title="Наше решение"
      subtitle="Мы разработали сервис для проведения вебинаров прямо в интерфейсе мессенджера Telegram, решающий ключевые проблемы организаторов"
    >
      <Grid container spacing={6} alignItems="center">
        <Grid item xs={12} md={6}>
          <Box>
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={feature.delay}
              />
            ))}
          </Box>

          <AnimatedElement animation="fadeInUp" delay={1}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<CheckCircleIcon />}
              sx={{ mt: 2 }}
            >
              Попробовать бесплатно
            </Button>
          </AnimatedElement>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <AnimatedElement animation="zoomIn" delay={0.5}>
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                bgcolor: 'background.paper',
                p: 4,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '8px',
                  bgcolor: theme.palette.secondary.main,
                }
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                  Результаты внедрения
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <AnimatedElement animation="fadeInUp" delay={0.7}>
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        p: 3, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 4
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        color="primary.main" 
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        +20%
                      </Typography>
                      <Typography variant="body1">
                        Увеличение доходимости зрителей до вебинара
                      </Typography>
                    </Box>
                  </AnimatedElement>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <AnimatedElement animation="fadeInUp" delay={0.8}>
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        p: 3, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 4
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        color="primary.main" 
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        100%
                      </Typography>
                      <Typography variant="body1">
                        Верифицированных контактов зрителей
                      </Typography>
                    </Box>
                  </AnimatedElement>
                </Grid>
                
                <Grid item xs={12}>
                  <AnimatedElement animation="fadeInUp" delay={0.9}>
                    <Box 
                      sx={{ 
                        textAlign: 'center', 
                        p: 3, 
                        bgcolor: alpha(theme.palette.secondary.main, 0.05),
                        borderRadius: 4,
                        border: `1px dashed ${alpha(theme.palette.secondary.main, 0.3)}`
                      }}
                    >
                      <Typography 
                        variant="h5" 
                        color="secondary.main" 
                        sx={{ fontWeight: 'bold', mb: 1 }}
                      >
                        Экономия до 10 000₽
                      </Typography>
                      <Typography variant="body1">
                        На каждые 100 привлеченных участников
                      </Typography>
                    </Box>
                  </AnimatedElement>
                </Grid>
              </Grid>
            </Paper>
          </AnimatedElement>
        </Grid>
      </Grid>
    </SectionContainer>
  );
};

export default SolutionSection; 